// Pre-mount URL rewriter for the CC OAuth callback (P-047 remediation,
// 2026-05-27).
//
// CC's OAuth redirect lands the browser at the path form
// `${BASE_URL}/auth/cc-callback?code=...&state=...` (per RFC 6749 §3.1.2
// fragment identifiers are forbidden in redirect URIs; CC enforces; see
// `src/lib/cc-callback-path.ts`). Our SPA uses a hash router (D-085);
// the path-form URL doesn't match any route + would 404 in the router.
//
// This shim runs ONCE at boot (called from `src/app/main.tsx` before
// `render()`). If the current URL matches the callback path, it uses
// `history.replaceState` to swap the URL into the hash form
// (`${BASE_URL}/#/auth/cc-callback?code=...&state=...`) WITHOUT a page
// reload. The hash router's first read of `window.location.hash` then
// sees the hash form + dispatches `<CCCallbackScreen />`, which reads
// the code + state from the (now-hash-embedded) query string via its
// existing handleCallback() path.
//
// **Why history.replaceState over window.location.replace** — the
// latter does a full navigation (re-fetches index.html, re-runs the
// bundle, loses any in-memory state). The former just swaps the URL
// in-place; the next render reads the new location synchronously.
// No flicker, no extra network round-trip.
//
// **Why before render** — `useRoute()` reads `window.location.hash` on
// mount. If the URL is still in the path form when `<AuthGate>` renders,
// `parseHash('')` returns `unknown` → `RouterOutlet` redirects to `#/`
// (Home) and the callback params are LOST. Running the shim before
// render guarantees the hash form is in place by the time any component
// reads location.
//
// **Idempotent / no-op when not a callback** — every page load runs the
// shim, but the pathname check exits in one comparison on non-callback
// loads. Production cost is negligible.
//
// **Test coverage** — colocated in cc-callback-redirect-shim.test.ts.
// happy-dom's `window.location` is mutable + history.replaceState is
// implemented; the test seam works without browser-specific stubs.

import { CC_CALLBACK_PATH } from '../lib/cc-callback-path'

/**
 * If `window.location.pathname` matches the CC OAuth callback path,
 * `history.replaceState` the URL into the hash form so the hash router
 * picks it up. No-op otherwise.
 *
 * Returns `true` if a rewrite happened (useful for tests + diagnostics);
 * `false` on no-op. Production callers ignore the return value.
 */
export function rewriteOAuthCallbackUrlIfPresent(): boolean {
  if (window.location.pathname !== CC_CALLBACK_PATH) return false
  const search = window.location.search // e.g., '?code=X&state=Y' (may be '')
  const hashForm = `${window.location.origin}/#${CC_CALLBACK_PATH}${search}`
  window.history.replaceState(null, '', hashForm)
  return true
}
